import { c as _c } from "react/compiler-runtime";
import { getEntitiesByFpIdOnboardingsByOnboardingIdTraceOptions } from '@onefootprint/fetch-dashboard';
import type { LitePlaybook } from '@onefootprint/request-types/dashboard';
import { LinkButton } from '@onefootprint/ui';
import { useQuery } from '@tanstack/react-query';
import { cx } from 'class-variance-authority';
import { useGetPlaybookVersionDisplayName } from '@/hooks/use-get-playbook-version-display-name';
import { useOpenPage } from '@/hooks/use-open-page';
import useEntityId from '../entities/components/details/hooks/use-entity-id';
import useEntityKind from '../entities/components/details/hooks/use-entity-kind';
import { ACTIVE_NODE_QUERY_KEY } from '../playbook-builder/playbook-builder.constants';
type OnboardingTraceProps = {
  /** When provided, fetches and shows the related onboarding trace. */
  onboardingId?: string;
  /** When provided, also links to the playbook for this trace. */
  playbook?: Pick<LitePlaybook, 'playbookId' | 'name' | 'version' | 'id' | 'isHidden'>;
  label?: string;
  nodeId?: string;
  className?: string;
  /**
   * Skips the trace fetch when `false` — that request 400s for an onboarding with no graph. Pass
   * `EntityOnboarding.hasGraphTrace` where it's known; omit it when it isn't, which fetches.
   */
  hasGraphTrace?: boolean;
};

/** When clicked, shows the execution trace for the `onboardingId` if it exists. Otherwise, navigates to the playbook details page for the `playbook`, if provided. */
export const OnboardingTraceLink = t0 => {
  const $ = _c(27);
  const {
    onboardingId,
    playbook,
    label,
    nodeId,
    className,
    hasGraphTrace
  } = t0;
  const fpId = useEntityId();
  const entityKind = useEntityKind();
  const handleOpenPage = useOpenPage();
  const {
    getPlaybookVersionDisplayName
  } = useGetPlaybookVersionDisplayName();
  const t1 = onboardingId ?? "";
  let t2;
  if ($[0] !== fpId || $[1] !== t1) {
    t2 = getEntitiesByFpIdOnboardingsByOnboardingIdTraceOptions({
      path: {
        fpId,
        onboardingId: t1
      }
    });
    $[0] = fpId;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = !!onboardingId && hasGraphTrace !== false;
  let t4;
  if ($[3] !== t2 || $[4] !== t3) {
    t4 = {
      ...t2,
      enabled: t3
    };
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const {
    data: trace
  } = useQuery(t4);
  let t5;
  if ($[6] !== entityKind?.urlPath || $[7] !== fpId || $[8] !== handleOpenPage || $[9] !== nodeId || $[10] !== onboardingId || $[11] !== playbook || $[12] !== trace) {
    t5 = event => {
      if (onboardingId && trace) {
        const query = nodeId ? {
          [ACTIVE_NODE_QUERY_KEY]: nodeId
        } : undefined;
        handleOpenPage(event, `/${entityKind?.urlPath}/${fpId}/onboardings/${onboardingId}/trace`, query);
      } else {
        if (playbook) {
          const {
            playbookId,
            id
          } = playbook;
          const qs = playbook.isHidden ? undefined : {
            version: id
          };
          handleOpenPage(event, `/playbooks/${playbookId}`, qs);
        }
      }
    };
    $[6] = entityKind?.urlPath;
    $[7] = fpId;
    $[8] = handleOpenPage;
    $[9] = nodeId;
    $[10] = onboardingId;
    $[11] = playbook;
    $[12] = trace;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const handleClick = t5;
  let t6;
  if ($[14] !== getPlaybookVersionDisplayName || $[15] !== label || $[16] !== playbook || $[17] !== trace?.kind) {
    t6 = () => {
      if (label) {
        return label;
      }
      return getPlaybookVersionDisplayName(playbook, trace?.kind);
    };
    $[14] = getPlaybookVersionDisplayName;
    $[15] = label;
    $[16] = playbook;
    $[17] = trace?.kind;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  const getLabel = t6;
  let t7;
  if ($[19] !== className) {
    t7 = cx("text-label-2 max-md:max-w-full max-md:whitespace-normal max-md:text-left", className);
    $[19] = className;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== getLabel) {
    t8 = getLabel();
    $[21] = getLabel;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  let t9;
  if ($[23] !== handleClick || $[24] !== t7 || $[25] !== t8) {
    t9 = <LinkButton className={t7} onClick={handleClick}>{t8}</LinkButton>;
    $[23] = handleClick;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    t9 = $[26];
  }
  return t9;
};